import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, ChevronDown } from 'lucide-react';
import { LANGUAGES, type LanguageCode } from '@/i18n/languages';
import { markManualLanguage } from '@/i18n/manualLanguage';

interface Props {
  /** `compact` is the in-app topbar variant (transparent pill, no
   *  border); `full` is the marketing-header variant (bordered card).
   *  Both show flag + native name on the trigger so the user always
   *  sees which language they're in — generic "globe + code" was
   *  unhelpful for non-Latin scripts. */
  variant?: 'compact' | 'full';
}

/**
 * Glassy popover language picker. Used in the public SiteHeader and
 * the post-login AppShell topbar. Replaces the legacy native <select>.
 */
export function LanguageSwitcher({ variant = 'full' }: Props) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const current = (i18n.language?.split('-')[0] || 'en') as LanguageCode;
  const meta = LANGUAGES.find((l) => l.code === current);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const label = meta?.nativeName ?? 'English';
  const flag = meta?.flag ?? '🌐';

  return (
    <div className="relative" ref={popoverRef}>
      <button
        aria-label="Language"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={
          variant === 'compact'
            ? 'inline-flex h-9 items-center gap-1.5 rounded-full px-2.5 text-sm text-foreground hover:bg-muted transition'
            : 'inline-flex h-9 items-center gap-1.5 rounded-xl border border-border bg-card px-3 text-xs font-medium hover:bg-muted transition'
        }
      >
        <span className="text-base leading-none">{flag}</span>
        {/* On the tiniest screens the topbar gets crowded — hide the
            label below sm. The flag alone still communicates which
            language is active. */}
        <span className={variant === 'compact' ? 'hidden sm:inline text-[13px] font-medium' : ''}>
          {label}
        </span>
        <ChevronDown className="h-3 w-3 opacity-60" />
      </button>

      <AnimatePresence>
        {open && (
          <motion.ul
            role="listbox"
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            className="absolute end-0 top-11 z-50 max-h-80 w-56 overflow-y-auto keenez-scrollbar
                       rounded-2xl glass-strong shadow-card-lift p-1"
          >
            {LANGUAGES.map((l) => {
              const active = l.code === current;
              return (
                <li key={l.code}>
                  <button
                    role="option"
                    aria-selected={active}
                    onClick={() => {
                      markManualLanguage();
                      void i18n.changeLanguage(l.code);
                      setOpen(false);
                    }}
                    className={`w-full flex items-center gap-2.5 rounded-xl px-3 py-2 text-sm text-start transition ${
                      active ? 'bg-primary/10 text-primary font-semibold' : 'hover:bg-muted text-foreground/80'
                    }`}
                  >
                    <span className="text-base">{l.flag}</span>
                    <span className="flex-1 truncate">{l.nativeName}</span>
                    {active && <Check className="h-3.5 w-3.5 text-primary" />}
                  </button>
                </li>
              );
            })}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}
